import { toastError } from "@/lib/toast-error";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useGetTemplate, useCreateTemplate, useUpdateTemplate, useSendTemplatePreviewEmail, TemplateField, TemplateFieldType, TemplatePurpose, TechnologyTemplate, TemplateBodyFormat } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Plus, Trash2, GripVertical, Save, Loader2, AlertTriangle, ChevronDown, Eye, Code2, Send } from "lucide-react";
import { Link } from "wouter";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useGetMe } from "@workspace/api-client-react";
import { computeDrift, extractAutoRegion } from "@/lib/mergeTagUtils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const fieldSchema = z.object({
  key: z.string().min(1, "Key is required").regex(/^[a-zA-Z0-9_]+$/, "Only letters, numbers, and underscores"),
  label: z.string().min(1, "Label is required"),
  type: z.enum(["text", "textarea", "number", "select"]),
  required: z.boolean().optional(),
  helpText: z.string().optional(),
  options: z.string().optional()
});

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  technology: z.string().min(1, "Technology is required"),
  purpose: z.enum(["registration", "follow_up", "internal_notification", "requester_summary"]),
  bodyFormat: z.enum(["text", "html"]).default("text"),
  description: z.string().optional(),
  subjectTemplate: z.string().min(1, "Subject template is required"),
  bodyTemplate: z.string().min(1, "Body template is required"),
  fields: z.array(fieldSchema).optional()
});

const PURPOSE_VARIABLES: Record<TemplatePurpose, string[]> = {
  registration: ["accountName", "opportunityName", "mrr", "tcv", "closeDate", "primaryContactName", "supplier.name", "supplier.accountName", "sender.name"],
  follow_up: ["accountName", "opportunityName", "supplier.name", "supplier.accountName", "daysSinceSent", "priorFollowUps", "originalSubject", "sender.name"],
  internal_notification: ["accountName", "opportunityName", "supplier.name", "supplier.accountName", "rep.name", "rep.email", "deal.url", "registration.technology"],
  requester_summary: ["accountName", "opportunityName", "mrr", "tcv", "primaryContactName", "primaryContactEmail", "notes", "requester.name", "requester.email", "#each registrations → name, accountName, email, templateName, sentAt, subject, body"],
};

const SYSTEM_SAMPLE_VALUES: Record<string, string> = {
  accountName: "Acme Corporation",
  opportunityName: "Q2 Solar Expansion",
  mrr: "$5,000/mo",
  tcv: "$60,000",
  closeDate: "June 30, 2026",
  primaryContactName: "Jane Smith",
  primaryContactEmail: "jane.smith@acme.com",
  notes: "Preferred partner, fast-track deal.",
  "supplier.name": "Power Solutions Inc.",
  "supplier.accountName": "Power Solutions",
  "sender.name": "Alex Johnson",
  daysSinceSent: "7",
  priorFollowUps: "1",
  originalSubject: "Deal Registration Confirmation",
  "rep.name": "Chris Lee",
  "rep.email": "chris.lee@company.com",
  "deal.url": "https://app.example.com/deals/12345",
  "registration.technology": "Solar",
  "requester.name": "Jordan Rivera",
  "requester.email": "jordan.rivera@company.com",
};

/** Tag-key regex: only real merge tags (e.g. "accountName", "supplier.name").
 *  Excludes the documentation entries like "#each registrations → ..." we
 *  surface in PURPOSE_VARIABLES for requester_summary. */
const TAG_KEY_RE = /^[\w.]+$/;

function buildSampleContext(
  purposeVars: string[],
  fields: FormValues["fields"],
  overrides: Record<string, string> = {},
): Record<string, string> {
  const ctx: Record<string, string> = {};
  const apply = (key: string, fallback: string) => {
    const o = overrides[key];
    ctx[key] = o && o.length > 0 ? o : fallback;
  };
  for (const v of purposeVars) {
    if (!TAG_KEY_RE.test(v)) continue;
    apply(v, SYSTEM_SAMPLE_VALUES[v] ?? `[${v}]`);
  }
  for (const f of (fields ?? [])) {
    if (f.key) {
      apply(f.key, f.label ? `Sample ${f.label}` : `[${f.key}]`);
    }
  }
  return ctx;
}

function substituteMergeTags(template: string, context: Record<string, string>): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (_, key: string) => {
    const trimmed = key.trim();
    return Object.prototype.hasOwnProperty.call(context, trimmed)
      ? context[trimmed]
      : `{{${trimmed}}}`;
  });
}

type FormValues = z.infer<typeof formSchema>;

const EMPTY_DEFAULTS: FormValues = {
  name: "",
  technology: "",
  purpose: "registration",
  bodyFormat: "text",
  description: "",
  subjectTemplate: "",
  bodyTemplate: "",
  fields: []
};

function templateToFormValues(t: TechnologyTemplate): FormValues {
  return {
    name: t.name,
    technology: t.technology,
    purpose: (t.purpose ?? "registration") as TemplatePurpose,
    bodyFormat: ((t.bodyFormat ?? "text") as TemplateBodyFormat),
    description: t.description || "",
    subjectTemplate: t.subjectTemplate,
    bodyTemplate: t.bodyTemplate,
    fields: t.fields?.map(f => ({
      key: f.key,
      label: f.label,
      type: f.type,
      required: f.required,
      helpText: f.helpText,
      options: f.options?.join(", ") || ""
    })) || []
  };
}

interface TemplateFormProps {
  initialValues: FormValues;
  isNew: boolean;
  id?: string;
  onSaved: () => void;
}

/** Detect renames by index: same position, old key gone, new key brand-new. */
function detectClientRenames(
  originalKeys: string[],
  currentKeys: string[],
): Array<{ oldKey: string; newKey: string }> {
  const originalSet = new Set(originalKeys);
  const currentSet = new Set(currentKeys);
  const renames: Array<{ oldKey: string; newKey: string }> = [];
  for (let i = 0; i < Math.min(originalKeys.length, currentKeys.length); i++) {
    const o = originalKeys[i]!;
    const n = currentKeys[i]!;
    if (o !== n && !currentSet.has(o) && !originalSet.has(n)) {
      renames.push({ oldKey: o, newKey: n });
    }
  }
  return renames;
}

function AutoManagedPreview({ body, format }: { body: string; format: "text" | "html" }) {
  const { auto } = extractAutoRegion(body);
  if (!auto.trim()) return null;
  const lines = auto.trim().split("\n").filter(Boolean);
  return (
    <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 space-y-1">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-primary/60 mb-2">
        Auto-managed fields block (appended on save, rendered as {format === "html" ? "HTML" : "plain text"})
      </div>
      {lines.map((line, i) => (
        <div key={i} className="text-xs font-mono text-muted-foreground">{line}</div>
      ))}
    </div>
  );
}

function HtmlPreview({ html, purposeVars, fields, sampleOverrides }: { html: string; purposeVars: string[]; fields: FormValues["fields"]; sampleOverrides: Record<string, string> }) {
  const ctx = buildSampleContext(purposeVars, fields, sampleOverrides);
  const rendered = substituteMergeTags(html, ctx);
  return (
    <div className="rounded-md border bg-white dark:bg-zinc-900 p-4 min-h-32 text-sm overflow-auto">
      <div
        className="prose prose-sm dark:prose-invert max-w-none"
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    </div>
  );
}

function DriftWarnings({ subject, body, fields }: { subject: string; body: string; fields: FormValues["fields"] }) {
  const tagFields = (fields ?? []).filter(f => f.key).map(f => ({ key: f.key, label: f.label }));
  const warnings = computeDrift(subject, body, tagFields);
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-2 text-sm rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <span className="text-amber-900 dark:text-amber-300">{w.message}</span>
        </div>
      ))}
    </div>
  );
}

interface CursorPos {
  start: number;
  end: number;
}

function SampleValuesPanel({
  purposeVars,
  fields,
  overrides,
  onChange,
  onResetAll,
}: {
  purposeVars: string[];
  fields: FormValues["fields"];
  overrides: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onResetAll: () => void;
}) {
  const systemTags = purposeVars.filter((v) => TAG_KEY_RE.test(v));
  const fieldTags = (fields ?? []).filter((f) => f.key);
  const hasOverrides = Object.values(overrides).some((v) => v && v.length > 0);

  const renderRow = (key: string, placeholder: string) => (
    <div key={key} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-2 items-center">
      <Label className="text-xs font-mono text-muted-foreground truncate" title={`{{${key}}}`}>
        {`{{${key}}}`}
      </Label>
      <Input
        value={overrides[key] ?? ""}
        placeholder={placeholder}
        className="h-8 text-sm"
        onChange={(e) => onChange(key, e.target.value)}
        data-testid={`sample-override-${key}`}
      />
    </div>
  );

  return (
    <Card data-testid="sample-values-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle>Customize Sample Values</CardTitle>
            <CardDescription>
              Override the sample values used in the preview and the test email. Leave a row blank to use the
              built-in default. Values are kept for this editing session only.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs h-7 shrink-0"
            onClick={onResetAll}
            disabled={!hasOverrides}
            data-testid="sample-overrides-reset"
          >
            Reset
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {systemTags.length > 0 && (
          <div className="space-y-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              System Variables
            </div>
            {systemTags.map((key) => renderRow(key, SYSTEM_SAMPLE_VALUES[key] ?? `[${key}]`))}
          </div>
        )}
        {fieldTags.length > 0 && (
          <div className="space-y-2 pt-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Extra Fields
            </div>
            {fieldTags.map((f) =>
              renderRow(f.key, f.label ? `Sample ${f.label}` : `[${f.key}]`),
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InsertVariablePicker({
  fields,
  purposeVars,
  subjectRef,
  bodyRef,
  subjectCursor,
  bodyCursor,
  lastFocused,
  onInsert,
}: {
  fields: FormValues["fields"];
  purposeVars: string[];
  subjectRef: React.RefObject<HTMLInputElement | null>;
  bodyRef: React.RefObject<HTMLTextAreaElement | null>;
  subjectCursor: React.RefObject<CursorPos>;
  bodyCursor: React.RefObject<CursorPos>;
  lastFocused: "subject" | "body";
  onInsert: (tag: string, target: "subject" | "body", start: number, end: number) => void;
}) {
  const fieldVars = (fields ?? []).filter(f => f.key).map(f => ({
    label: f.label || f.key,
    key: f.key,
  }));

  const handleSelect = (key: string) => {
    const tag = `{{${key}}}`;
    if (lastFocused === "subject") {
      const pos = subjectCursor.current ?? { start: 0, end: 0 };
      onInsert(tag, "subject", pos.start, pos.end);
      // Restore focus and cursor
      requestAnimationFrame(() => {
        const el = subjectRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(pos.start + tag.length, pos.start + tag.length);
        }
      });
    } else {
      const pos = bodyCursor.current ?? { start: 0, end: 0 };
      onInsert(tag, "body", pos.start, pos.end);
      requestAnimationFrame(() => {
        const el = bodyRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(pos.start + tag.length, pos.start + tag.length);
        }
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="text-xs h-7 shrink-0">
          Insert variable <ChevronDown className="ml-1 h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto w-60">
        {fieldVars.length > 0 && (
          <>
            <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Extra Fields</div>
            {fieldVars.map(f => (
              <DropdownMenuItem key={f.key} className="font-mono text-xs" onSelect={() => handleSelect(f.key)}>
                <span>{`{{${f.key}}}`}</span>
                <span className="ml-auto text-muted-foreground text-[10px] pl-2">{f.label}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}
        <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">System Variables</div>
        {purposeVars.map(v => (
          <DropdownMenuItem key={v} className="font-mono text-xs" onSelect={() => handleSelect(v)}>
            {`{{${v}}}`}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface SortableFieldRowRenderProps {
  setNodeRef: (el: HTMLElement | null) => void;
  style: React.CSSProperties;
  attributes: React.HTMLAttributes<HTMLElement>;
  listeners: React.DOMAttributes<HTMLElement> | undefined;
  isDragging: boolean;
}

function SortableFieldRow({
  id,
  children,
}: {
  id: string;
  children: (props: SortableFieldRowRenderProps) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return <>{children({ setNodeRef, style, attributes, listeners, isDragging })}</>;
}

function TemplateForm({ initialValues, isNew, id, onSaved }: TemplateFormProps) {
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const sendTestEmail = useSendTemplatePreviewEmail();
  const { data: meForTest } = useGetMe();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "fields"
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    move(oldIndex, newIndex);
    form.setValue("fields", form.getValues("fields"), { shouldDirty: true });
  };

  // Track original field keys (at form load time) for rename detection
  const originalFieldKeys = useRef<string[]>(
    (initialValues.fields ?? []).map(f => f.key)
  );

  // Cursor position tracking for Insert Variable
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectCursor = useRef<CursorPos>({ start: 0, end: 0 });
  const bodyCursor = useRef<CursorPos>({ start: 0, end: 0 });
  const [lastFocused, setLastFocused] = useState<"subject" | "body">("body");

  // Confirmation dialogs
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState<number | null>(null);
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string>("");
  const [renameConfirm, setRenameConfirm] = useState<{
    renames: Array<{ oldKey: string; newKey: string }>;
    data: FormValues;
  } | null>(null);

  // Per-tag overrides for the in-page preview and "Send test" email. Session-only;
  // not persisted to the server. Empty string == "use the canned default".
  const [sampleOverrides, setSampleOverrides] = useState<Record<string, string>>({});
  const handleSampleOverrideChange = (key: string, value: string) => {
    setSampleOverrides((prev) => ({ ...prev, [key]: value }));
  };
  const handleResetSampleOverrides = () => setSampleOverrides({});

  const handleInsert = (tag: string, target: "subject" | "body", start: number, end: number) => {
    const field = target === "subject" ? "subjectTemplate" : "bodyTemplate";
    const current = form.getValues(field) ?? "";
    const newVal = current.slice(0, start) + tag + current.slice(end);
    form.setValue(field, newVal, { shouldDirty: true, shouldValidate: false });
  };

  const doSave = (data: FormValues) => {
    const payload = {
      ...data,
      bodyFormat: data.bodyFormat ?? "text",
      fields: data.fields?.map(f => ({
        ...f,
        type: f.type as TemplateFieldType,
        options: f.type === 'select' && f.options ? f.options.split(',').map(s => s.trim()).filter(Boolean) : undefined
      })) as TemplateField[]
    };

    const onSuccess = (saved: TechnologyTemplate) => {
      if (saved.bodyTemplate) form.setValue("bodyTemplate", saved.bodyTemplate, { shouldDirty: false });
      if (saved.subjectTemplate) form.setValue("subjectTemplate", saved.subjectTemplate, { shouldDirty: false });
      // Refresh originalFieldKeys so subsequent rename-detect is relative to the saved state
      originalFieldKeys.current = (saved.fields ?? []).map((f) => f.key);
      toast.success(isNew ? "Template created successfully" : "Template updated successfully");
      onSaved();
    };
    const onError = (err: unknown) => {
      toastError(err, isNew ? "Failed to create template" : "Failed to update template");
    };

    if (isNew) {
      createTemplate.mutate({ data: payload }, { onSuccess, onError });
    } else {
      if (!id) return;
      updateTemplate.mutate({ id, data: payload }, { onSuccess, onError });
    }
  };

  const onSubmit = (data: FormValues) => {
    if (!isNew) {
      const currentKeys = (data.fields ?? []).map(f => f.key);
      const renames = detectClientRenames(originalFieldKeys.current, currentKeys);
      if (renames.length > 0) {
        setRenameConfirm({ renames, data });
        return;
      }
    }
    doSave(data);
  };

  const confirmRenameAndSave = () => {
    if (!renameConfirm) return;
    const data = renameConfirm.data;
    setRenameConfirm(null);
    doSave(data);
  };

  const handleDeleteField = (index: number) => {
    const key = form.getValues(`fields.${index}.key`);
    if (key) {
      setPendingDeleteIdx(index);
      setPendingDeleteKey(key);
    } else {
      remove(index);
    }
  };

  const confirmDelete = () => {
    if (pendingDeleteIdx !== null) {
      remove(pendingDeleteIdx);
      setPendingDeleteIdx(null);
      setPendingDeleteKey("");
    }
  };

  const isSaving = createTemplate.isPending || updateTemplate.isPending;
  const isSendingTest = sendTestEmail.isPending;

  const handleSendTest = async () => {
    const valid = await form.trigger(["subjectTemplate", "bodyTemplate"]);
    if (!valid) {
      toast.error("Please provide a subject and body before sending a test.");
      return;
    }
    const data = form.getValues();
    // Drop empty entries so the server keeps the canned defaults for those keys.
    const overridesPayload = Object.fromEntries(
      Object.entries(sampleOverrides).filter(([, v]) => typeof v === "string" && v.length > 0),
    );
    sendTestEmail.mutate(
      {
        data: {
          subjectTemplate: data.subjectTemplate,
          bodyTemplate: data.bodyTemplate,
          bodyFormat: (data.bodyFormat ?? "text") as TemplateBodyFormat,
          purpose: data.purpose,
          fields: (data.fields ?? []).map(f => ({
            key: f.key,
            label: f.label,
            type: f.type as TemplateFieldType,
            required: f.required,
            helpText: f.helpText,
            options: f.type === "select" && f.options ? f.options.split(",").map(s => s.trim()).filter(Boolean) : undefined,
          })) as TemplateField[],
          ...(Object.keys(overridesPayload).length > 0 ? { sampleValueOverrides: overridesPayload } : {}),
        },
      },
      {
        onSuccess: (result) => {
          toast.success(`Test email sent to ${result.deliveredTo}`);
        },
        onError: (err) => {
          toastError(err, "Failed to send test email");
        },
      },
    );
  };
  const purposeWatched = form.watch("purpose") as TemplatePurpose;
  const bodyFormatWatched = (form.watch("bodyFormat") ?? "text") as "text" | "html";
  const subjectWatched = form.watch("subjectTemplate");
  const bodyWatched = form.watch("bodyTemplate");
  const fieldsWatched = form.watch("fields");
  const purposeVars = PURPOSE_VARIABLES[purposeWatched] ?? PURPOSE_VARIABLES.registration;

  return (
    <>
      {/* Rename confirmation dialog */}
      <Dialog open={!!renameConfirm} onOpenChange={(open) => !open && setRenameConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm field rename</DialogTitle>
            <DialogDescription>
              The following field key{renameConfirm && renameConfirm.renames.length > 1 ? "s were" : " was"} renamed.
              All <code>{"{{oldKey}}"}</code> references in the subject and body will be updated automatically.
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-1 py-2">
            {renameConfirm?.renames.map(r => (
              <li key={r.oldKey} className="font-mono">
                <span className="text-destructive">{`{{${r.oldKey}}}`}</span>
                <span className="text-muted-foreground mx-2">→</span>
                <span className="text-primary">{`{{${r.newKey}}}`}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameConfirm(null)}>Cancel</Button>
            <Button onClick={confirmRenameAndSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save with renames
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete field confirmation dialog */}
      <Dialog open={pendingDeleteIdx !== null} onOpenChange={(open) => !open && setPendingDeleteIdx(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove field?</DialogTitle>
            <DialogDescription>
              This will remove the field <code className="font-mono">{pendingDeleteKey}</code> from the template.
              Any <code className="font-mono">{`{{${pendingDeleteKey}}}`}</code> references in the subject or body
              will no longer resolve to a value when emails are sent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIdx(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Remove field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>General Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Template Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Standard UCaaS Registration" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="technology"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Technology Category</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., UCaaS, CCaaS, SD-WAN" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="purpose"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Purpose</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="tpl-purpose-select">
                              <SelectValue placeholder="Select purpose" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="registration">Registration (to supplier)</SelectItem>
                            <SelectItem value="follow_up">Follow-up (to supplier)</SelectItem>
                            <SelectItem value="internal_notification">Internal Notification (to deal owner)</SelectItem>
                            <SelectItem value="requester_summary">Requester Summary (to sender)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Determines where this template appears and which merge variables are available.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="bodyFormat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Body Format</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="tpl-body-format-select">
                              <SelectValue placeholder="Select format" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="text">Plain text</SelectItem>
                            <SelectItem value="html">HTML</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Plain text is simpler and automatically converted to HTML for email clients. HTML gives full control over formatting.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description (Optional)</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Brief description of when to use this template..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle>Email Content</CardTitle>
                      <CardDescription className="mt-1">
                        System variables:{" "}
                        {purposeVars.slice(0, 3).map((v, i) => (
                          <span key={v}>
                            {i > 0 && ", "}
                            <code className="bg-muted px-1 rounded text-xs">{`{{${v}}}`}</code>
                          </span>
                        ))}
                        {purposeVars.length > 3 && <span> &amp; {purposeVars.length - 3} more.</span>}
                        {" "}Extra fields are auto-inserted below the body on save.
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={handleSendTest}
                        disabled={isSendingTest}
                        title={meForTest?.email ? `Send a test email to ${meForTest.email}` : "Send a test email to yourself"}
                        data-testid="tpl-send-test-btn"
                      >
                        {isSendingTest ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        Send test
                      </Button>
                      <InsertVariablePicker
                        fields={fieldsWatched}
                        purposeVars={purposeVars}
                        subjectRef={subjectRef}
                        bodyRef={bodyRef}
                        subjectCursor={subjectCursor}
                        bodyCursor={bodyCursor}
                        lastFocused={lastFocused}
                        onInsert={handleInsert}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="subjectTemplate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Deal Registration: {{accountName}}"
                            {...field}
                            ref={(el) => {
                              field.ref(el);
                              (subjectRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                            }}
                            onFocus={() => setLastFocused("subject")}
                            onSelect={(e) => {
                              const el = e.currentTarget;
                              subjectCursor.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
                            }}
                            onKeyUp={(e) => {
                              const el = e.currentTarget;
                              subjectCursor.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="bodyTemplate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Body</FormLabel>
                        {bodyFormatWatched === "html" ? (
                          <Tabs defaultValue="edit">
                            <TabsList className="mb-2">
                              <TabsTrigger value="edit" className="text-xs gap-1">
                                <Code2 className="h-3 w-3" /> Edit HTML
                              </TabsTrigger>
                              <TabsTrigger value="preview" className="text-xs gap-1">
                                <Eye className="h-3 w-3" /> Preview
                              </TabsTrigger>
                            </TabsList>
                            <TabsContent value="edit">
                              <FormControl>
                                <Textarea
                                  rows={14}
                                  placeholder="<p>Hi <strong>{{supplier.name}}</strong>,</p>"
                                  className="font-mono text-sm"
                                  {...field}
                                  ref={(el) => {
                                    field.ref(el);
                                    (bodyRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
                                  }}
                                  onFocus={() => setLastFocused("body")}
                                  onSelect={(e) => {
                                    const el = e.currentTarget;
                                    bodyCursor.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
                                  }}
                                  onKeyUp={(e) => {
                                    const el = e.currentTarget;
                                    bodyCursor.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
                                  }}
                                />
                              </FormControl>
                            </TabsContent>
                            <TabsContent value="preview">
                              <HtmlPreview html={field.value ?? ""} purposeVars={purposeVars} fields={fieldsWatched} sampleOverrides={sampleOverrides} />
                              <p className="text-[11px] text-muted-foreground mt-1.5">
                                Previewed with sample values. Real values are substituted on send.
                              </p>
                            </TabsContent>
                          </Tabs>
                        ) : (
                          <FormControl>
                            <Textarea
                              rows={14}
                              placeholder="Hi {{supplier.name}}, ..."
                              className="font-mono text-sm"
                              {...field}
                              ref={(el) => {
                                field.ref(el);
                                (bodyRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
                              }}
                              onFocus={() => setLastFocused("body")}
                              onSelect={(e) => {
                                const el = e.currentTarget;
                                bodyCursor.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
                              }}
                              onKeyUp={(e) => {
                                const el = e.currentTarget;
                                bodyCursor.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
                              }}
                            />
                          </FormControl>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <AutoManagedPreview body={bodyWatched ?? ""} format={bodyFormatWatched} />

                  <DriftWarnings
                    subject={subjectWatched ?? ""}
                    body={bodyWatched ?? ""}
                    fields={fieldsWatched}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-8">
              <Card>
                <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
                  <div className="min-w-0">
                    <CardTitle>Extra Fields</CardTitle>
                    <CardDescription>Custom fields collected before sending. Each field's tag is auto-inserted into the email body on save.</CardDescription>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="self-start sm:self-auto shrink-0"
                    onClick={() => append({ key: "", label: "", type: "text", required: false, helpText: "", options: "" })}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Field
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {fields.length === 0 && (
                    <div className="text-sm text-muted-foreground border-2 border-dashed rounded-lg p-8 text-center">
                      No extra fields defined.
                    </div>
                  )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields.map(f => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                  {fields.map((f, index) => (
                    <SortableFieldRow key={f.id} id={f.id}>
                      {({ setNodeRef, style, attributes, listeners, isDragging }) => (
                    <div
                      ref={setNodeRef}
                      style={style}
                      className={`border rounded-lg p-4 space-y-3 relative bg-background ${isDragging ? "shadow-lg z-10" : ""}`}
                      data-testid={`field-row-${index}`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          className="mt-2 cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground shrink-0"
                          aria-label="Drag to reorder field"
                          data-testid={`field-drag-${index}`}
                          {...attributes}
                          {...listeners}
                        >
                          <GripVertical className="h-5 w-5" />
                        </button>
                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <FormField
                            control={form.control}
                            name={`fields.${index}.key`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">Key</FormLabel>
                                <FormControl><Input placeholder="seats" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`fields.${index}.label`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">Label</FormLabel>
                                <FormControl><Input placeholder="Approx. seat count" {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`fields.${index}.type`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">Type</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                                  <SelectContent>
                                    <SelectItem value="text">Text</SelectItem>
                                    <SelectItem value="textarea">Textarea</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="select">Select</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`fields.${index}.required`}
                            render={({ field }) => (
                              <FormItem className="flex items-end gap-2 pb-2">
                                <FormControl>
                                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                </FormControl>
                                <Label className="text-xs">Required</Label>
                              </FormItem>
                            )}
                          />
                          {form.watch(`fields.${index}.type`) === "select" && (
                            <FormField
                              control={form.control}
                              name={`fields.${index}.options`}
                              render={({ field }) => (
                                <FormItem className="col-span-2">
                                  <FormLabel className="text-xs">Options (comma-separated)</FormLabel>
                                  <FormControl><Input placeholder="Voice, Chat, Email" {...field} /></FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                          <FormField
                            control={form.control}
                            name={`fields.${index}.helpText`}
                            render={({ field }) => (
                              <FormItem className="col-span-2">
                                <FormLabel className="text-xs">Help Text (Optional)</FormLabel>
                                <FormControl><Input {...field} /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <Button type="button" size="icon" variant="ghost" onClick={() => handleDeleteField(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                      )}
                    </SortableFieldRow>
                  ))}
                    </SortableContext>
                  </DndContext>
                </CardContent>
              </Card>

              <SampleValuesPanel
                purposeVars={purposeVars}
                fields={fieldsWatched}
                overrides={sampleOverrides}
                onChange={handleSampleOverrideChange}
                onResetAll={handleResetSampleOverrides}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 sticky bottom-0 bg-background pt-4 border-t">
            <Link href="/templates"><Button type="button" variant="outline">Cancel</Button></Link>
            <Button type="submit" disabled={isSaving} data-testid="tpl-save-btn">
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {isNew ? "Create Template" : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
}

export default function TemplateEdit() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const [, setLocation] = useLocation();
  const { data: me, isLoading: isLoadingMe } = useGetMe();

  const { data: template, isLoading } = useGetTemplate(id as string, {
    query: { enabled: !isNew && !!id, queryKey: ["/api/templates", id as string] }
  });

  useEffect(() => {
    if (!isLoadingMe && me && !me.isAdmin) {
      toast.error("You do not have permission to manage templates.");
      setLocation("/templates");
    }
  }, [me, isLoadingMe, setLocation]);

  if (!isNew && (isLoading || !template)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const initialValues = isNew ? EMPTY_DEFAULTS : templateToFormValues(template!);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl mx-auto pb-20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Link href="/templates">
            <Button variant="ghost" size="icon" className="shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{isNew ? "New Template" : "Edit Template"}</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Configure registration email structure.</p>
          </div>
        </div>
      </div>

      <TemplateForm
        initialValues={initialValues}
        isNew={isNew}
        id={id}
        onSaved={() => setLocation("/templates")}
      />
    </div>
  );
}
